import { Button } from "@blueskydataplatform/shared-ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@blueskydataplatform/shared-ui/components/ui/card";
import { Input } from "@blueskydataplatform/shared-ui/components/ui/input";
import { BillingEmailField } from "@blueskydataplatform/shared-ui/components/billing-email-field";
import { zodResolver } from "@hookform/resolvers/zod";
import Cookies from "js-cookie";
import { AlertCircle } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useForm, useFormState, useWatch, type FieldErrors } from "react-hook-form";
import { useParams } from "react-router";
import { z } from "zod";
import type { SignUpFieldDescriptor } from "../types/sign-up-fields";
import { isExtensionFieldRequired, isSignUpFieldVisible } from "../utils/is-extension-field-required";
import {
  isValidFoundingYear,
  isValidSignUpUrl,
} from "@blueskydataplatform/shared-ui/utils/validate-sign-up-field-format";
import DynamicSignUpFields from "./dynamic-sign-up-fields";

import Logo from "@/components/logo";
import { UILink } from "@/components/ui-link";
import { paths } from "@/config/paths";
import { getSafeRedirect } from "@/lib/utils";
import { usePreserveQuery } from "@/hooks/use-preserve-query";
import useQueryParams, { SIGN_IN_QUERY } from "@/hooks/use-query-params";
import { useMcpProvider } from "@/providers/mcp-providers-context";
import { Checkbox } from "@blueskydataplatform/shared-ui/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@blueskydataplatform/shared-ui/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@blueskydataplatform/shared-ui/components/ui/select";
import { Separator } from "@blueskydataplatform/shared-ui/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@blueskydataplatform/shared-ui/components/ui/alert";
import { useToast } from "@blueskydataplatform/shared-ui/hooks/use-toast";
import type {
  CatalogUseCase,
  NormalizedSignUpConsentCheckboxesConfig,
} from "@blueskydataplatform/shared-ui/types/platform.types";
import { SignUpConsentCheckboxLabel } from "@blueskydataplatform/shared-ui/components/sign-up-fields/sign-up-consent-checkbox-label";
import {
  marketingConsentFromCheckbox,
  normalizeSignUpConsentCheckboxes,
} from "@blueskydataplatform/shared-ui/utils/sign-up-consent-checkboxes";
import { useNavigate } from "react-router";
import { useSignUp, type SignUpInput } from "../api/sign-up";
import { SIGN_UP_TITLE_VALUES } from "../constants/sign-up-title";
import { getIsBadgeSolid, getUiBranding } from "@blueskydataplatform/shared-ui/theme/ui-branding";

const baseSchema = z.object({
  email: z.string().email({ message: "Invalid email" }),
  title: z.enum(SIGN_UP_TITLE_VALUES, { required_error: "Please select a title" }),
  firstName: z.string().trim().min(1, { message: "First name is required" }),
  lastName: z.string().trim().min(1, { message: "Last name is required" }),
  useCaseId: z.number({ required_error: "Please select a use case" }).int().positive().optional(),
  orgType: z.string(),
  role: z.string(),
  company: z.string().optional(),
  /** Optional; blank means invoices go to the account email. Business use cases only. */
  billingEmail: z.string().optional(),
  employeesCount: z.string().optional(),
  password: z.string().min(8, { message: "At least 8 characters" }),
  inviteCode: z.string().optional(),
  termsAccepted: z.boolean().optional(),
  subscribeToMarketingEmails: z.boolean().optional(),
  isFromMCP: z.boolean().optional(),
  provider: z.string().optional(),
  signUpExtensions: z.record(z.string(), z.union([z.string(), z.boolean()]).nullish()).optional(),
});

type FormValues = z.infer<typeof baseSchema>;
type SignUpExtensionsFormRecord = NonNullable<FormValues["signUpExtensions"]>;

const VALIDATION_ALERT_MESSAGE = "Please fix the errors highlighted below before creating your account.";

function getFirstErrorFieldName(errors: FieldErrors): string | null {
  const walk = (node: FieldErrors, path: string[] = []): string | null => {
    for (const key of Object.keys(node)) {
      const field = node[key];
      if (!field || typeof field !== "object") continue;
      if ("message" in field && field.message) {
        return [...path, key].join(".");
      }
      const nested = walk(field as FieldErrors, [...path, key]);
      if (nested) return nested;
    }
    return null;
  };
  return walk(errors);
}

function buildSignUpFormSchema(
  descriptorsRef: RefObject<SignUpFieldDescriptor[]>,
  consentRef: RefObject<NormalizedSignUpConsentCheckboxesConfig>
) {
  return baseSchema.superRefine((data, ctx) => {
    const consent = consentRef.current;
    if (consent.termsOfService.enabled && consent.termsOfService.required && !data.termsAccepted) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["termsAccepted"],
        message: consent.termsOfService.requiredMessage ?? "You must accept the Terms of Service",
      });
    }
    if (consent.marketingEmails.enabled && consent.marketingEmails.required) {
      const marketingChecked = marketingConsentFromCheckbox(
        Boolean(data.subscribeToMarketingEmails),
        consent.marketingEmails.optOut
      );
      if (!marketingChecked) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["subscribeToMarketingEmails"],
          message: consent.marketingEmails.requiredMessage ?? "This field is required",
        });
      }
    }

    // Only when the field is shown. Personal orgs omit it from the payload; a leftover
    // typo from a previous business use case must not block submit behind a hidden input.
    if (!data.inviteCode && data.orgType === "business") {
      const billingTrimmed = (data.billingEmail ?? "").trim();
      if (billingTrimmed && !z.string().email().safeParse(billingTrimmed).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["billingEmail"],
          message: "Enter a valid billing email address",
        });
      }
    }

    if (!data.inviteCode) {
      if (data.useCaseId == null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["useCaseId"], message: "Please select a use case" });
      }

      // Validate dynamic extension fields — only when a use case is selected (not invite-code flow).
      const descriptors = descriptorsRef.current;
      const exts = data.signUpExtensions ?? {};
      const allowed = new Set(descriptors.map(d => d.name));

      for (const key of Object.keys(exts)) {
        if (!allowed.has(key)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["signUpExtensions", key], message: "Unknown field" });
        }
      }
      for (const d of descriptors) {
        const val = exts[d.name];
        // A field hidden by its `showWhen` controller is never required; a value submitted into
        // one anyway still has to pass the rules below.
        const isReq = isExtensionFieldRequired(d) && isSignUpFieldVisible(d, exts);
        if (isReq && (val === undefined || val === null || val === "")) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["signUpExtensions", d.name],
            message: `${d.label} is required`,
          });
        }
        if (val === undefined || val === null) continue;

        // Optional fields are often "" (text/select) or whitespace; skip rules / option checks.
        if (!isReq && typeof val === "string" && val.trim() === "") {
          continue;
        }

        if (d.type === "address") {
          if (typeof val !== "string") {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["signUpExtensions", d.name],
              message: `${d.label} is invalid`,
            });
            continue;
          }
          const trimmed = val.trim();
          if (!isReq && trimmed === "") continue;
          try {
            const parsed = JSON.parse(trimmed) as { complete?: boolean; value?: { line1?: string; country?: string } };
            if (!isReq && !parsed?.complete) continue;
            if (isReq && !parsed?.complete) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["signUpExtensions", d.name],
                message: `Please complete ${d.label}`,
              });
            }
            if (isReq && parsed?.complete && parsed.value) {
              const { line1, country } = parsed.value;
              if (!line1?.trim() || !country?.trim()) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: ["signUpExtensions", d.name],
                  message: `${d.label} is incomplete`,
                });
              }
            }
          } catch {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["signUpExtensions", d.name],
              message: `${d.label} is invalid`,
            });
          }
          continue;
        }

        if (d.type === "country") {
          if (typeof val !== "string") {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["signUpExtensions", d.name],
              message: `${d.label} is invalid`,
            });
            continue;
          }
          const trimmed = val.trim();
          if (!isReq && trimmed === "") continue;
          continue;
        }

        if (typeof val === "string" && d.rules) {
          const { minLength, maxLength, pattern } = d.rules;
          if (minLength != null && val.length < minLength)
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["signUpExtensions", d.name],
              message: `Minimum ${minLength} characters`,
            });
          if (maxLength != null && val.length > maxLength)
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["signUpExtensions", d.name],
              message: `Maximum ${maxLength} characters`,
            });
          if (pattern != null && !new RegExp(pattern).test(val))
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["signUpExtensions", d.name],
              message: "Invalid format",
            });
          if (d.rules.format === "url" && !isValidSignUpUrl(val)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["signUpExtensions", d.name],
              message: `${d.label} must be a valid URL`,
            });
          }
          if (d.rules.format === "year" && !isValidFoundingYear(val)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["signUpExtensions", d.name],
              message: `${d.label} must be a year up to ${new Date().getFullYear()}`,
            });
          }
        }
        if ((d.type === "select" || d.type === "selectList") && d.options && val !== undefined) {
          const valid = d.options.map(o => o.value);
          if (!valid.includes(val as string))
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["signUpExtensions", d.name],
              message: "Invalid option",
            });
        }
      }
    }
  });
}

/** Fills empty required-looking selectList fields with the first option so the UI shows a selection. */
function applySelectListDefaults(
  descriptors: SignUpFieldDescriptor[],
  exts: SignUpExtensionsFormRecord | undefined
): SignUpExtensionsFormRecord {
  const next = { ...(exts ?? {}) };
  for (const d of descriptors) {
    if (d.type !== "selectList" || !d.options?.length) continue;
    next[d.name] = d.options[0].value;
  }
  return next;
}

function signUpExtensionsForApi(exts: FormValues["signUpExtensions"]): Record<string, string | boolean> | undefined {
  if (!exts || Object.keys(exts).length === 0) return undefined;
  const out: Record<string, string | boolean> = {};
  for (const [k, v] of Object.entries(exts)) {
    if (v === undefined || v === null) continue;
    out[k] = v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function buildPersonFullName(firstName: string, lastName: string): string {
  return `${firstName.trim()} ${lastName.trim()}`.trim();
}

function resolveOrgName(
  descriptors: SignUpFieldDescriptor[],
  signUpExtensions: FormValues["signUpExtensions"],
  firstName: string,
  lastName: string
): string {
  const personName = buildPersonFullName(firstName, lastName);
  const hasCompanyField = descriptors.some(d => d.name === "company");
  if (!hasCompanyField) return personName;
  const company = signUpExtensions?.company;
  if (typeof company === "string" && company.trim()) return company.trim();
  return personName;
}

const PlatformSignUpForm = ({
  useCases,
  initialUseCase,
  consentCheckboxes: consentCheckboxesProp,
}: {
  useCases: CatalogUseCase[];
  initialUseCase: CatalogUseCase | undefined;
  consentCheckboxes?: NormalizedSignUpConsentCheckboxesConfig;
}) => {
  const { inviteCode: inviteCodeQuery, referrerCode, partner, email } = useQueryParams();
  const { inviteCode: inviteCodeParam } = useParams<{ inviteCode?: string }>();
  const inviteCode = inviteCodeParam || inviteCodeQuery || null;
  const { isFromMCP, state: mcpState } = useQueryParams();
  const { provider } = useMcpProvider();
  const uiBranding = getUiBranding();
  const consentCheckboxes = consentCheckboxesProp ?? normalizeSignUpConsentCheckboxes();
  const consentBranding = useMemo(
    () => ({
      companyName: uiBranding.companyName,
      termsUrl: uiBranding.links?.terms,
      privacyUrl: uiBranding.links?.privacy,
    }),
    [uiBranding.companyName, uiBranding.links?.privacy, uiBranding.links?.terms]
  );
  const activeDescriptorsRef = useRef<SignUpFieldDescriptor[]>(initialUseCase?.extraSignUpFields ?? []);
  const consentCheckboxesRef = useRef(consentCheckboxes);
  consentCheckboxesRef.current = consentCheckboxes;
  const signUpFormSchema = useMemo(() => buildSignUpFormSchema(activeDescriptorsRef, consentCheckboxesRef), []);
  const resolver = useMemo(() => zodResolver(signUpFormSchema), [signUpFormSchema]);
  const form = useForm<FormValues>({
    resolver,
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      inviteCode: inviteCode || undefined,
      email: email || "",
      title: SIGN_UP_TITLE_VALUES[0],
      firstName: "",
      lastName: "",
      useCaseId: initialUseCase?.id,
      orgType: initialUseCase?.isForBusinesses ? "business" : "individual",
      role: initialUseCase?.isForBusinesses ? "company" : "individual-investor",
      password: "",
      billingEmail: "",
      termsAccepted: false,
      subscribeToMarketingEmails: consentCheckboxes.marketingEmails.optOut,
      isFromMCP,
      provider: provider?.id,
    },
  });

  const isSolid = getIsBadgeSolid(uiBranding);
  const selectedUseCaseId = useWatch({ name: "useCaseId", control: form.control });
  // Set from the selected use case's `isForBusinesses`; personal orgs are a single account,
  // so they have no second address to bill.
  const isCommercial = useWatch({ name: "orgType", control: form.control }) === "business";

  // Live value, so the billing field's "leave blank to use ..." text tracks what was typed above.
  const accountEmail = useWatch({ name: "email", control: form.control }) ?? "";
  const { errors, isSubmitted, isValid } = useFormState({ control: form.control });
  const rootErrorMessage = errors.root?.message;
  const [validationAlert, setValidationAlert] = useState<string | null>(null);
  const validationAlertRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (isSubmitted && isValid) {
      setValidationAlert(null);
    }
  }, [isSubmitted, isValid]);
  const navigate = useNavigate();

  const dynamicDescriptors = useMemo(() => {
    if (inviteCode) return [];
    return useCases.find(u => u.id === selectedUseCaseId)?.extraSignUpFields ?? [];
  }, [inviteCode, selectedUseCaseId, useCases]);

  useEffect(() => {
    if (inviteCode) return;
    if (selectedUseCaseId == null) return;
    const useCase = useCases.find(u => u.id === selectedUseCaseId);
    if (!useCase) return;
    const descriptors = useCase.extraSignUpFields ?? [];
    const current = form.getValues("signUpExtensions") ?? {};
    const next = applySelectListDefaults(descriptors, current);
    if (JSON.stringify(next) !== JSON.stringify(current)) {
      form.setValue("signUpExtensions", next, { shouldValidate: false });
    }
    const roleExt = next["role"];
    if (typeof roleExt === "string" && roleExt && form.getValues("role") !== roleExt) {
      form.setValue("role", roleExt, { shouldValidate: true, shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `form` from useForm is stable
  }, [inviteCode, selectedUseCaseId, useCases]);

  const preserveQuery = usePreserveQuery();
  const registerMutation = useSignUp({
    mutationConfig: {
      onSuccess: response => {
        const redirect = getSafeRedirect(response.data.redirect);
        if (redirect) {
          window.location.href = redirect;
          return;
        }
        const url = preserveQuery(paths.finishRegistration.getHref());
        navigate(url);
      },
      onError: (err: any) => {
        if (err.response && err.response.status === 400) {
          const error = err.response.data;
          // The server does not say which rule refused the address, so show its message
          // verbatim and put it on the field the user has to change.
          if (error.code === "EMAIL_NOT_ALLOWED") {
            form.setError("email", { type: "manual", message: error.error });
            form.setFocus("email");
            toast.error(error.error);
            return;
          }
          if (error.field === "email" && error.code.indexOf("EXISTS") > -1) {
            const message = "An account with this email already exists. Please use a different email address.";
            form.setError("email", { type: "manual", message });
            form.setFocus("email");
            toast.error(message);
            return;
          }
          if (error.field === "password") {
            const message = "Please use a less common password.";
            form.setError("password", { type: "manual", message });
            form.setFocus("password");
            toast.error(message);
            return;
          }
          if (error.code === "BAD_INVITE") {
            const message = "Your invitation is not valid. Please contact the account administrator.";
            form.setError("root", { type: "manual", message });
            toast.error(message);
            return;
          }
          if (error.code === "ER_DUP_ENTRY") {
            const message = "An account with this email already exists.";
            form.setError("email", { type: "manual", message });
            form.setFocus("email");
            toast.error(message);
            return;
          }
        }
        // Fallback error message for any unhandled errors
        const error = err.response?.data;
        const message =
          error?.message || error?.error || "An error occurred while creating your account. Please try again.";
        form.setError("root", { type: "manual", message });
        toast.error(message);
      },
    },
  });

  const onInvalid = (fieldErrors: FieldErrors<FormValues>) => {
    setValidationAlert(VALIDATION_ALERT_MESSAGE);
    requestAnimationFrame(() => {
      validationAlertRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
    const firstField = getFirstErrorFieldName(fieldErrors);
    if (firstField) {
      void form.setFocus(firstField as Parameters<typeof form.setFocus>[0]);
    }
  };

  const onSubmit = (values: FormValues) => {
    setValidationAlert(null);
    const cookieReferrerCode = Cookies.get("cloud-refid");
    const cookiePartner = Cookies.get("cloud-partner");

    const firstName = values.firstName.trim();
    const lastName = values.lastName.trim();
    const accountFullName = buildPersonFullName(firstName, lastName);

    const payload: SignUpInput = {
      inviteCode,
      referrerCode: referrerCode || cookieReferrerCode || null,
      partner: partner || cookiePartner || null,
      email: values.email,
      title: values.title,
      firstName,
      lastName,
      fullName: accountFullName,
      companyName: resolveOrgName(dynamicDescriptors, values.signUpExtensions, firstName, lastName),
      useCaseId: values.useCaseId ?? null,
      userType:
        typeof values.signUpExtensions?.role === "string" && values.signUpExtensions.role !== ""
          ? values.signUpExtensions.role
          : values.role,
      orgType: values.orgType,
      // Omitted when blank so the server falls back to the account email, and never sent
      // for a personal org whose field was hidden.
      billingEmail: isCommercial ? (values.billingEmail ?? "").trim() || undefined : undefined,
      password: values.password,
      hasCheckedVal: false, // honeypot field
      isFromMCP,
      provider: provider?.id,
      state: mcpState,
      signUpExtensions: signUpExtensionsForApi(values.signUpExtensions),
      subscribeToMarketingEmails: consentCheckboxes.marketingEmails.enabled ? values.subscribeToMarketingEmails : false,
    };
    registerMutation.mutate({ data: payload });
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-xl">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-2">
            <Logo className="mb-4" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">Create your account</CardTitle>
          <CardDescription className="text-center">Sign up to get started</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-4">
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email *</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="Enter email" {...field} disabled={!!inviteCode || !!partner} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title *</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select title" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {SIGN_UP_TITLE_VALUES.map(t => (
                            <SelectItem key={t} value={t}>
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-6">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>First Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="First name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="col-span-6">
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Last Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="Last name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                {!inviteCode && (
                  <>
                    <FormField
                      control={form.control}
                      name="useCaseId"
                      render={() => (
                        <FormItem>
                          <FormLabel>What best describes your use case? *</FormLabel>
                          <div className={`grid grid-cols-${(useCases || [])?.length || 1} gap-2`}>
                            {(useCases || []).map(useCase => (
                              <Button
                                key={useCase.id}
                                type="button"
                                variant={selectedUseCaseId === useCase.id ? "default" : "outline"}
                                onClick={() => {
                                  form.setValue("useCaseId", useCase.id, { shouldValidate: true, shouldDirty: true });
                                  form.setValue("orgType", useCase.isForBusinesses ? "business" : "individual", {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  });
                                  form.setValue("role", useCase.isForBusinesses ? "company" : "individual-investor", {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  });
                                  // Hidden for personal orgs, same as dropping extension fields that
                                  // don't belong to the new use case.
                                  if (!useCase.isForBusinesses) {
                                    form.setValue("billingEmail", "", { shouldValidate: true, shouldDirty: true });
                                  }
                                  // Sync ref with new descriptor list.
                                  activeDescriptorsRef.current = useCase.extraSignUpFields ?? [];

                                  // Clear any stale extension values from the previous use case.
                                  const newNames = new Set(
                                    (useCase.extraSignUpFields ?? []).map((d: SignUpFieldDescriptor) => d.name)
                                  );
                                  const currentExts = form.getValues("signUpExtensions") ?? {};
                                  const cleared: SignUpExtensionsFormRecord = {};
                                  for (const key of Object.keys(currentExts)) {
                                    if (newNames.has(key)) cleared[key] = currentExts[key];
                                  }
                                  const withSelectDefaults = applySelectListDefaults(
                                    useCase.extraSignUpFields ?? [],
                                    cleared
                                  );
                                  form.setValue("signUpExtensions", withSelectDefaults, { shouldValidate: false });
                                  const roleExt = withSelectDefaults["role"];
                                  if (typeof roleExt === "string" && roleExt) {
                                    form.setValue("role", roleExt, { shouldValidate: true, shouldDirty: true });
                                  }
                                }}
                              >
                                {useCase.displayName}
                              </Button>
                            ))}
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
                {!inviteCode && <DynamicSignUpFields descriptors={dynamicDescriptors} form={form} />}

                {/* Invited teammates join an existing org whose billing email is already set,
                    so the field would be ignored server-side. */}
                {!inviteCode && isCommercial && (
                  <FormField
                    control={form.control}
                    name="billingEmail"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormControl>
                          <BillingEmailField
                            value={field.value ?? ""}
                            fallbackEmail={accountEmail.trim()}
                            error={fieldState.error?.message}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password *</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="Create a password" {...field} />
                      </FormControl>
                      <p className="text-xs text-gray-500">Must have at least eight characters</p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {consentCheckboxes.termsOfService.enabled ? (
                <FormField
                  control={form.control}
                  name="termsAccepted"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-start space-x-3">
                        <FormControl>
                          <Checkbox checked={Boolean(field.value)} onCheckedChange={v => field.onChange(Boolean(v))} />
                        </FormControl>
                        <FormLabel className="font-normal">
                          <SignUpConsentCheckboxLabel
                            config={consentCheckboxes.termsOfService}
                            branding={consentBranding}
                            fallback={
                              <>
                                I agree to the {uiBranding.companyName}{" "}
                                <a
                                  href={uiBranding.links?.terms}
                                  target="_blank"
                                  className="underline"
                                  rel="noreferrer"
                                >
                                  Terms of Service
                                </a>
                              </>
                            }
                          />
                        </FormLabel>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}

              {consentCheckboxes.marketingEmails.enabled ? (
                <FormField
                  control={form.control}
                  name="subscribeToMarketingEmails"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-start space-x-3">
                        <FormControl>
                          <Checkbox
                            checked={marketingConsentFromCheckbox(
                              Boolean(field.value),
                              consentCheckboxes.marketingEmails.optOut
                            )}
                            onCheckedChange={v =>
                              field.onChange(
                                marketingConsentFromCheckbox(Boolean(v), consentCheckboxes.marketingEmails.optOut)
                              )
                            }
                          />
                        </FormControl>
                        <FormLabel className="font-normal">
                          <SignUpConsentCheckboxLabel
                            config={consentCheckboxes.marketingEmails}
                            branding={consentBranding}
                            fallback={
                              <>
                                I would like to receive emails from {uiBranding.companyName} on products, services, news
                                and events.
                              </>
                            }
                          />
                        </FormLabel>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}

              {validationAlert ? (
                <div ref={validationAlertRef}>
                  <Alert variant="destructive" solid={isSolid}>
                    <AlertCircle />
                    <AlertTitle>Check your entries</AlertTitle>
                    <AlertDescription>{validationAlert}</AlertDescription>
                  </Alert>
                </div>
              ) : null}

              {rootErrorMessage != null && rootErrorMessage !== "" ? (
                <Alert variant="destructive" solid={isSolid}>
                  <AlertCircle />
                  <AlertTitle>Unable to create account</AlertTitle>
                  <AlertDescription>{String(rootErrorMessage)}</AlertDescription>
                </Alert>
              ) : null}

              <div className="space-y-4">
                <Button type="submit" className="w-full mt-4" disabled={registerMutation.isPending}>
                  {registerMutation.isPending ? "Creating account..." : "Create account"}
                </Button>
                <div className="text-center text-sm">
                  <UILink to={preserveQuery(paths.home.getHref(), SIGN_IN_QUERY)} className="text-sm">
                    Return to sign in
                  </UILink>
                </div>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlatformSignUpForm;
